<template>
  <div class="management-page py-5">
    <div class="container">
      
      <div class="text-center mb-5">
        <h2 class="section-title text-primary text-uppercase fw-bold">
          <i class="fa fa-briefcase me-2"></i> {{ $t("management.title") }}
        </h2>
      </div>

      <div class="filter-card card border-0 shadow-sm mb-5 mx-auto" style="max-width: 600px;">
        <div class="card-body p-4">
          <label class="fw-bold mb-2 text-muted text-uppercase small">{{ $t("management.select_category") }}</label>
          
          <div class="position-relative custom-select-wrapper">
            <div class="input-group cursor-pointer" @click="toggleDropdown">
              <span class="input-group-text bg-white border-end-0">
                <i class="fa fa-sitemap text-primary"></i>
              </span>
              
              <input 
                type="text" 
                class="form-control border-start-0 ps-0 bg-white cursor-pointer"
                :value="displayCategoryName"
                readonly
              >
              
              <span class="input-group-text bg-white">
                <i class="fa fa-chevron-down small text-muted" :class="{ 'rotate-180': showDropdown }"></i>
              </span>
            </div>

            <ul v-if="showDropdown" class="custom-dropdown-list shadow">
              <li 
                @click="selectCategory('organigrama')" 
                class="dropdown-item fw-bold text-primary bg-light-hover"
                :class="{ 'active-item': selectedCategorie === 'organigrama' }"
              >
                <i class="fa fa-thumbtack me-2"></i> {{ $t("management.organigram") }}
              </li>
              
              <li 
                v-for="cat in categorii" 
                :key="cat.cod" 
                @click="selectCategory(cat)"
                class="dropdown-item"
                :class="{ 'active-item': selectedCategorie === cat.cod }"
              >
                {{ getLocalizedCategory(cat) }}
              </li>
            </ul>
          </div>
          <div v-if="showDropdown" class="click-overlay" @click="showDropdown = false"></div>
        </div>
      </div>

      <div v-if="selectedCategorie === 'organigrama'" class="fade-in">
        <div class="card border-0 shadow-lg overflow-hidden">
          <div class="card-header bg-primary text-white text-center py-3">
            <h5 class="mb-0 fw-bold"><i class="fa fa-users me-2"></i> {{ $t("management.organigram") }}</h5>
          </div>
          <div class="card-body text-center p-5">
            <div v-if="organigrama && organigrama.imagine">
              <div class="mb-4">
                <img :src="organigrama.imagine" alt="Organigrama" class="img-fluid rounded shadow-sm border" style="max-height: 600px;">
              </div>
              <div class="d-flex justify-content-center gap-3 align-items-center">
                <a v-if="organigrama.document" :href="organigrama.document" target="_blank" class="btn btn-primary btn-lg rounded-pill px-4">
                  <i class="fa fa-download me-2"></i> {{ $t("management.download_organigram") }}
                </a>
                <span class="text-muted small" v-if="organigrama.data_upload">
                  <i class="fa fa-clock-o me-1"></i> {{ formatDate(organigrama.data_upload) }}
                </span>
              </div>
            </div>
            <div v-else class="alert alert-light border py-4">
              <i class="fa fa-info-circle fa-2x text-muted mb-3"></i>
              <p class="mb-0">{{ $t("management.no_organigram") }}</p>
            </div>
          </div>
        </div>
      </div>

      <div v-else class="fade-in">
        <h4 class="mb-4 text-center text-secondary border-bottom pb-2 d-inline-block mx-auto">
          {{ displayCategoryName }}
        </h4>

        <div v-if="documente.length > 0" class="document-grid">
          <div v-for="document in documente" :key="document.id" class="document-item">
            <div class="card doc-card h-100 border-0 shadow-sm">
              <div class="card-body d-flex flex-column text-center p-4">
                <div class="icon-wrapper mb-3">
                  <i class="fa fa-file-text-o fa-3x text-primary opacity-75"></i>
                </div>
                <h6 class="card-title fw-bold text-dark mb-3 flex-grow-1">
                  {{ getLocalizedText(document) }}
                </h6>
                <a :href="document.document" target="_blank" class="btn btn-outline-primary w-100 rounded-pill mt-2">
                  <i class="fa fa-download me-2"></i> Descarcă
                </a>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="alert alert-light border text-center py-5">
          <i class="fa fa-folder-open-o fa-2x text-muted mb-3"></i>
          <h5 class="text-muted">{{ $t("management.no_documents") }}</h5>
        </div>

        <div v-if="totalPages > 1" class="pagination-container mt-5">
          <nav>
            <ul class="pagination pagination-custom">
              <li class="page-item" :class="{ disabled: page === 1 }">
                <button class="page-link" @click="changePage(page - 1)"><i class="fa fa-chevron-left"></i></button>
              </li>
              <li v-for="n in totalPages" :key="n" class="page-item" :class="{ active: n === page }">
                <button class="page-link fw-bold" @click="changePage(n)">{{ n }}</button>
              </li>
              <li class="page-item" :class="{ disabled: page === totalPages }">
                <button class="page-link" @click="changePage(page + 1)"><i class="fa fa-chevron-right"></i></button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      documente: [],
      categorii: [],
      organigrama: null,
      
      // State Simplificat
      selectedCategorie: "organigrama", 
      showDropdown: false,
      // Nu mai avem nevoie de searchTerms sau filter logic

      page: 1,
      totalPages: 1,
      perPage: 20,
    };
  },
  computed: {
    currentLang() {
      return this.$i18n.locale;
    },
    // Calculăm dinamic ce text să afișăm în input
    displayCategoryName() {
      if (this.selectedCategorie === 'organigrama') {
        return this.$t("management.organigram");
      }
      const cat = this.categorii.find(c => c.cod === this.selectedCategorie);
      return cat ? this.getLocalizedCategory(cat) : this.$t("management.select_category");
    }
  },
  methods: {
    async fetchCategorii() {
      try {
        const response = await axios.get("/categorii/conducere/");
        this.categorii = response.data;
      } catch (error) {
        console.error("Eroare la preluarea categoriilor:", error);
      }
    },

    // --- Dropdown Logic Simplificat ---
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    selectCategory(selection) {
      if (selection === 'organigrama') {
        this.selectedCategorie = 'organigrama';
        this.fetchOrganigrama();
      } else {
        // Selection este un obiect categorie
        this.selectedCategorie = selection.cod;
        this.fetchDocumente();
      }
      this.page = 1;
      this.showDropdown = false;
    },
    // ----------------------------------

    async fetchDocumente() {
      this.documente = [];
      try {
        const response = await axios.get(`/documente/conducere/`, {
          params: {
            categorie: this.selectedCategorie,
            page: this.page,
            page_size: this.perPage,
          },
        });
        this.documente = response.data.results ?? response.data;
        const totalCount = response.data.count ?? this.documente.length;
        this.totalPages = Math.ceil(totalCount / this.perPage);
      } catch (error) {
        console.error("Eroare documente:", error);
        this.documente = [];
      }
    },
    async fetchOrganigrama() {
      try {
        const response = await axios.get("/organigrama/");
        this.organigrama = response.data;
      } catch (error) {
        this.organigrama = null;
      }
    },
    getLocalizedCategory(categorie) {
      return categorie ? categorie[`nume_${this.currentLang}`] || categorie.nume_ro : "";
    },
    getLocalizedText(document) {
      return document[`nume_${this.currentLang}`] || document.nume_ro;
    },
    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.page = newPage;
        this.fetchDocumente();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
  },
  mounted() {
    this.fetchCategorii();
    this.fetchOrganigrama();
  },
};
</script>

<style scoped>
.management-page {
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Custom Select Styles */
.custom-select-wrapper { position: relative; }
.cursor-pointer { cursor: pointer; }

/* Rotim săgeata când e deschis */
.rotate-180 { transform: rotate(180deg); transition: transform 0.2s; }

.custom-dropdown-list {
  position: absolute;
  top: 100%; left: 0; right: 0;
  background: white;
  border-radius: 8px;
  margin-top: 5px;
  padding: 0;
  list-style: none;
  max-height: 300px;
  overflow-y: auto;
  z-index: 1000;
  border: 1px solid #eee;
}

.dropdown-item {
  padding: 12px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f8f9fa;
  transition: background 0.2s;
}

.dropdown-item:hover, .dropdown-item.active-item {
  background-color: #e9ecef;
  color: #004a99;
}

.click-overlay {
  position: fixed; top: 0; left: 0; width: 100%; height: 100%;
  z-index: 999; background: transparent;
}

/* Document Cards & Paginare (aceleași ca înainte) */
.document-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 20px;
}
.doc-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  background: white;
}
.doc-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
}
.icon-wrapper { height: 60px; display: flex; align-items: center; justify-content: center; }

.pagination-container { display: flex; justify-content: center; }
.pagination-custom .page-item { margin: 0 3px; }
.pagination-custom .page-link {
  border: none; border-radius: 8px !important; color: #004a99;
  min-width: 40px; height: 40px; display: flex; align-items: center; justify-content: center;
  transition: all 0.3s ease; background-color: white; box-shadow: 0 2px 5px rgba(0,0,0,0.05);
}
.pagination-custom .page-item.active .page-link {
  background-color: #004a99; color: white;
  box-shadow: 0 4px 10px rgba(0, 74, 153, 0.3);
}

.fade-in { animation: fadeIn 0.5s ease-in-out; }
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>